/**
 * BZB Quick Add to Cart
 * Handles variant selection, quantity, AJAX add-to-cart, and image swapping
 */

(function() {
  'use strict';

  // Initialize on DOM ready
  document.addEventListener('DOMContentLoaded', initBZBQuickAdd);

  function initBZBQuickAdd() {
    // Handle variant selectors
    document.querySelectorAll('.bzb-quick-add__select').forEach(select => {
      select.addEventListener('change', handleVariantChange);
    });

    // Handle quantity buttons
    document.querySelectorAll('.bzb-quick-add__qty-btn').forEach(btn => {
      btn.addEventListener('click', handleQuantityChange);
    });

    // Handle form submissions
    document.querySelectorAll('.bzb-quick-add-form').forEach(form => {
      form.addEventListener('submit', handleAddToCart);
    });
  }

  /**
   * Handle variant option changes
   */
  function handleVariantChange(e) {
    const select = e.target;
    const productId = select.dataset.productId;
    const card = select.closest('[data-product-card]');
    const form = select.closest('form');
    
    // Get product JSON
    const productJsonEl = document.querySelector(`[data-product-json-${productId}]`);
    if (!productJsonEl) return;
    
    let product;
    try {
      product = JSON.parse(productJsonEl.textContent);
    } catch (err) {
      console.error('Error parsing product JSON:', err);
      return;
    }
    
    // Get all selected options for this product
    const selects = form.querySelectorAll('.bzb-quick-add__select');
    const selectedOptions = Array.from(selects).map(s => s.value);
    
    // Find matching variant
    const variant = product.variants.find(v => {
      return selectedOptions.every((opt, index) => v.options[index] === opt);
    });
    
    if (variant) {
      // Update hidden variant ID input
      const variantInput = form.querySelector('[data-variant-id]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
      
      // Update product image
      const imageEl = card?.querySelector('[data-product-image]');
      if (imageEl) {
        if (variant.featured_image && variant.featured_image.src) {
          imageEl.src = variant.featured_image.src;
          imageEl.srcset = '';
        } else if (product.featured_image) {
          // Fallback to main product image
          imageEl.src = product.featured_image;
          imageEl.srcset = '';
        }
      }
      
      // Update price display
      const priceEl = card?.querySelector('[data-product-price]');
      if (priceEl) {
        let priceHTML = '';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          priceHTML += `<span class="bzb-grid__price-compare">${formatMoney(variant.compare_at_price)}</span>`;
        }
        priceHTML += `<span class="bzb-grid__price-current">${formatMoney(variant.price)}</span>`;
        priceEl.innerHTML = priceHTML;
      }
      
      // Update button state
      const submitBtn = form.querySelector('.bzb-quick-add__submit');
      const textEl = submitBtn?.querySelector('.bzb-quick-add__text');
      
      if (variant.available) {
        submitBtn.disabled = false;
        if (textEl) textEl.textContent = 'ADD TO CART';
      } else {
        submitBtn.disabled = true;
        if (textEl) textEl.textContent = 'SOLD OUT';
      }
    }
  }

  /**
   * Handle quantity +/- buttons
   */
  function handleQuantityChange(e) {
    const btn = e.target.closest('.bzb-quick-add__qty-btn');
    if (!btn) return;
    
    const wrapper = btn.closest('.bzb-quick-add__quantity, .bzb-quick-add__quantity--compact');
    const input = wrapper.querySelector('.bzb-quick-add__qty-input');
    const action = btn.dataset.action;
    let value = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || 99;
    
    if (action === 'increase' && value < max) {
      value++;
    } else if (action === 'decrease' && value > min) {
      value--;
    }
    
    input.value = value;
  }

  /**
   * Handle AJAX add to cart
   */
  async function handleAddToCart(e) {
    e.preventDefault();
    
    const form = e.target;
    const submitBtn = form.querySelector('.bzb-quick-add__submit');
    const textEl = submitBtn.querySelector('.bzb-quick-add__text');
    const loadingEl = submitBtn.querySelector('.bzb-quick-add__loading');
    const successEl = submitBtn.querySelector('.bzb-quick-add__success');
    
    // Store original text
    const originalText = textEl.textContent;
    
    // Show loading state
    submitBtn.disabled = true;
    textEl.style.display = 'none';
    loadingEl.style.display = 'inline-flex';
    successEl.style.display = 'none';
    
    try {
      const formData = new FormData(form);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Add to cart failed');
      }
      
      const data = await response.json();
      
      // Show success state
      loadingEl.style.display = 'none';
      successEl.style.display = 'inline';
      
      // Update cart count in header (if your theme supports it)
      updateCartCount();
      
      // Trigger custom event for theme integrations
      document.dispatchEvent(new CustomEvent('bzb:cart:added', { 
        detail: { item: data }
      }));

      // Trigger theme-specific cart events
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
      
      // Reset after delay
      setTimeout(() => {
        successEl.style.display = 'none';
        textEl.style.display = 'inline';
        textEl.textContent = originalText;
        submitBtn.disabled = false;
      }, 2000);
      
    } catch (error) {
      console.error('Add to cart error:', error);
      
      // Show error state
      loadingEl.style.display = 'none';
      textEl.style.display = 'inline';
      textEl.textContent = 'ERROR - TRY AGAIN';
      
      setTimeout(() => {
        textEl.textContent = originalText;
        submitBtn.disabled = false;
      }, 2000);
    }
  }

  /**
   * Update cart count in header
   */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Try common cart count selectors
      const countSelectors = [
        '.cart-count',
        '.cart-count-bubble',
        '[data-cart-count]',
        '.header__cart-count',
        '.site-header__cart-count',
        '#cart-icon-bubble span',
        '.cart-count-number',
        '.js-cart-count'
      ];
      
      countSelectors.forEach(selector => {
        const el = document.querySelector(selector);
        if (el) {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? '' : 'none';
        }
      });
      
      // Dispatch event for theme-specific handling
      document.dispatchEvent(new CustomEvent('bzb:cart:updated', { 
        detail: { cart }
      }));
      
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Format money (basic implementation)
   */
  function formatMoney(cents) {
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Basic fallback - assumes USD
    const amount = (cents / 100).toFixed(2);
    return '$' + amount;
  }

})();